/**
 * Validators
 * Centralized validation and sanitization functions
 */

import { FONT_SIZE_OPTIONS, TIMER } from '@config/constants'
import type { FontSize, TimerOption } from '../types'

/**
 * Validates that a number is within a specified range
 */
export function validateNumberInRange(value: number, min: number, max: number): boolean {
  if (!Number.isFinite(value)) {
    return false
  }
  return value >= min && value <= max
}

/**
 * Clamps a number to a specified range
 */
export function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min
  }
  return Math.max(min, Math.min(max, value))
}

/**
 * Safely parses an integer with validation
 */
export function safeParseInt(
  value: string | number,
  fallback: number,
  min?: number,
  max?: number
): number {
  const parsed = Number.parseInt(value as string, 10)
  if (!Number.isFinite(parsed)) {
    return fallback
  }
  if (min !== undefined && max !== undefined) {
    return clampNumber(parsed, min, max)
  }
  return parsed
}

/**
 * Validates imported data structure
 */
export function validateImportData(data: unknown): boolean {
  if (!data || typeof data !== 'object') {
    return false
  }

  const dataObj = data as Record<string, unknown>
  const hasSettings: boolean = Boolean(dataObj.settings && typeof dataObj.settings === 'object')
  const hasStats: boolean = Boolean(dataObj.stats && typeof dataObj.stats === 'object')

  return hasSettings || hasStats
}

/**
 * Sanitizes font option to valid value
 */
export function sanitizeFontOption(candidate: string, fallback: FontSize): FontSize {
  return FONT_SIZE_OPTIONS.includes(candidate as FontSize) ? (candidate as FontSize) : fallback
}

/**
 * Sanitizes max value within range
 */
export function sanitizeMaxValue(
  candidate: number | string,
  fallback: number,
  min: number,
  max: number
): number {
  const parsed = Number.parseInt(candidate as string, 10)
  if (!Number.isFinite(parsed)) {
    return fallback
  }
  return Math.max(min, Math.min(max, parsed))
}

/**
 * Sanitizes timer setting to valid value
 */
export function sanitizeTimerSetting(value: number | string, fallback: TimerOption = 0): TimerOption {
  const parsed = Number.parseInt(value as string, 10)
  if (!Number.isFinite(parsed)) {
    return fallback
  }
  if (!TIMER.VALID_VALUES.includes(parsed as TimerOption)) {
    return fallback
  }
  return parsed as TimerOption
}

/**
 * Validates that a string is not empty
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * Validates that a value is a valid array element from allowed list
 */
export function validateFromList<T extends string>(
  candidate: unknown,
  allowedValues: readonly T[],
  fallback: T
): T {
  if (typeof candidate !== 'string') {
    return fallback
  }
  const normalized = candidate.trim()
  return allowedValues.includes(normalized as T) ? (normalized as T) : fallback
}
